import React, { useEffect, useState } from 'react';
import { analyticsService, type AgentPerformanceMetrics } from '@/services/analyticsService';
import { Button } from '@/components/Button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const AgentPerformance: React.FC = () => {
  useDocumentTitle('Agent Performance | SupportDesk');
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState<AgentPerformanceMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    from.setHours(0, 0, 0, 0);
    setLoading(true);
    setError('');
    analyticsService.getAgentPerformance({ dateRange: { from, to }, includeInactive: true })
      .then(result => { if (current) setRows(result.data.agents); })
      .catch(reason => { if (current) { setRows([]); setError(reason instanceof Error ? reason.message : 'Unable to load agent performance.'); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [days, revision]);
  const exportRows = () => {
    const cell = (value: string | number) => '"' + String(value).replace(/^[=+@-]/, "'$&").replace(/"/g, '""') + '"';
    const csv = [
      ['Agent', 'Email', 'Unresolved tickets', 'Resolved in last 30 days', 'Average response (minutes)', 'Average resolution (hours)'],
      ...rows.map(row => [row.name, row.email, row.metrics.ticketsInProgress, row.metrics.ticketsResolved, row.metrics.averageResponseTime, row.metrics.averageResolutionTime])
    ].map(row => row.map(cell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `agent-performance-${days}-days.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6">
    <h1 className="text-2xl font-bold">Agent Performance</h1>
    <p className="text-gray-600">Metrics for tickets created during the selected period. Resolution counts include those resolved within the last 30 days. Response and resolution averages include only tickets with those events recorded.</p>
    <div className="flex gap-3 items-center">
      <label htmlFor="agent-period">Ticket creation period</label>
      <select id="agent-period" value={days} onChange={event => setDays(Number(event.target.value))} className="border rounded p-2">
        <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
      </select>
      <Button onClick={() => setRevision(value => value + 1)} disabled={loading}>Refresh</Button>
      <Button onClick={exportRows} disabled={loading || !!error || rows.length === 0}>Export CSV</Button>
    </div>
    {error && <div role="alert" className="text-red-700">{error}</div>}
    {loading ? <p role="status">Loading agent performance...</p> : !error && (
      rows.length === 0 ? <p>No agents found for this report.</p> : <div className="overflow-x-auto"><table className="w-full text-left">
        <thead><tr>{['Agent', 'Unresolved tickets', 'Resolved in last 30 days', 'Avg response (minutes)', 'Avg resolution (hours)'].map(label => <th className="p-3 border-b" key={label}>{label}</th>)}</tr></thead>
        <tbody>{rows.map(row => <tr key={row.agentId}>
          <td className="p-3 border-b">{row.name}<div className="text-sm text-gray-600">{row.email}</div></td>
          <td className="p-3 border-b">{row.metrics.ticketsInProgress}</td><td className="p-3 border-b">{row.metrics.ticketsResolved}</td>
          <td className="p-3 border-b">{row.metrics.averageResponseTime || 'No recorded responses'}</td>
          <td className="p-3 border-b">{row.metrics.averageResolutionTime || 'No recorded resolutions'}</td>
        </tr>)}</tbody>
      </table></div>
    )}
  </div>;
};
export default AgentPerformance;
